import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ActionButtonComponent } from '../../../../shared/components/action-button/action-button.component';
import { DateRange } from '../../../../shared/components/date-range-picker/date-range-picker.component';
import {
  FilterCategory,
  FilterModalComponent,
  FilterModalCustomRangeConfig,
} from '../../../../shared/components/filter-modal/filter-modal.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../../shared/components/pagination-bar/pagination-bar.component';
import { SearchbarComponent } from '../../../../shared/components/searchbar/searchbar.component';
import { todayIn } from '../../../../shared/utils/subscription-pause.util';
import { Company } from '../../../services/company.service';
import { CompanyContextService } from '../../../services/company-context.service';
import { MembershipPauseEventsService } from '../../../services/membership-pause-events.service';
import {
  DEFAULT_PAUSE_EVENT_FILTERS,
  MembershipPauseEventRow,
  PauseEventAppliedFilters,
} from '../../../models/membership-pause-event.model';
import {
  PAUSE_FILTER_CATEGORY,
  buildPauseEventFilterCategories,
  countActiveFilters,
  resolvePauseEventFiltersFromCategories,
  toPauseEventQuery,
  toPauseEventRow,
} from '../../../utils/membership-pause-event.util';

/**
 * Membership pause history for the company open in the detail shell (CWS-1648): one row per
 * pause event with its SMS delivery state. Read-only. Display rules live in
 * `membership-pause-event.util`; this component only holds view state.
 */
@Component({
  selector: 'app-company-pause-events',
  standalone: true,
  imports: [
    CommonModule,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    PaginationBarComponent,
  ],
  templateUrl: './company-pause-events.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-pause-events.component.scss'],
})
export class CompanyPauseEventsComponent implements OnInit, OnDestroy {
  private readonly pauseEventsService = inject(MembershipPauseEventsService);
  private readonly companyContext = inject(CompanyContextService);

  rows: MembershipPauseEventRow[] = [];
  loading = true;
  errorMessage = '';

  company: Company | null = null;

  searchTerm = '';
  appliedFilters: PauseEventAppliedFilters = { ...DEFAULT_PAUSE_EVENT_FILTERS };

  /** Filter modal state: edits stay in the draft until Apply, and are dropped on close. */
  showFilterModal = false;
  filterCategories: FilterCategory[] = buildPauseEventFilterCategories(this.appliedFilters);
  filterModalDateRange: DateRange = { startDate: null, endDate: null };

  currentPage = 1;
  itemsPerPage = 25;
  totalItems = 0;
  totalPages = 0;

  expandedRowId: string | null = null;

  private contextSub?: Subscription;
  private requestSub?: Subscription;

  ngOnInit(): void {
    let first = true;
    this.contextSub = this.companyContext.current$.subscribe(company => {
      const changed = first || (company?.id || '') !== (this.company?.id || '');
      first = false;
      this.company = company;
      if (changed) {
        this.currentPage = 1;
        this.loadEvents();
      }
    });
  }

  ngOnDestroy(): void {
    this.contextSub?.unsubscribe();
    this.requestSub?.unsubscribe();
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  get activeFilterCount(): number {
    return countActiveFilters(this.appliedFilters);
  }

  /** Date pickers in the Filter modal; pause events cannot be in the future (company day). */
  get filterModalCustomRangeConfig(): FilterModalCustomRangeConfig {
    return {
      categoryId: PAUSE_FILTER_CATEGORY.date,
      startLabel: 'Start Date',
      endLabel: 'End Date',
      startDate: this.filterModalDateRange.startDate,
      endDate: this.filterModalDateRange.endDate,
      maxDate: todayIn(this.company?.timezone),
    };
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.currentPage = 1;
    this.loadEvents();
  }

  onFilter(): void {
    this.syncFilterModalFromApplied();
    this.showFilterModal = true;
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
    this.syncFilterModalFromApplied();
  }

  onCustomRangeChange(range: DateRange): void {
    this.filterModalDateRange = {
      startDate: range.startDate ? new Date(range.startDate) : null,
      endDate: range.endDate ? new Date(range.endDate) : null,
    };
  }

  onFilterOptionChange(event: { categories: FilterCategory[] }): void {
    this.filterCategories = event.categories;
  }

  onApplyFilters(categories: FilterCategory[]): void {
    this.appliedFilters = resolvePauseEventFiltersFromCategories(
      categories,
      this.filterModalDateRange
    );
    this.showFilterModal = false;
    this.currentPage = 1;
    this.loadEvents();
  }

  private syncFilterModalFromApplied(): void {
    this.filterCategories = buildPauseEventFilterCategories(this.appliedFilters);
    this.filterModalDateRange = {
      startDate: this.appliedFilters.dateFrom,
      endDate: this.appliedFilters.dateTo,
    };
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEvents();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadEvents();
  }

  toggleExpanded(row: MembershipPauseEventRow): void {
    this.expandedRowId = this.expandedRowId === row.id ? null : row.id;
  }

  loadEvents(): void {
    this.requestSub?.unsubscribe();
    const companyId = this.company?.id;
    if (!companyId) {
      this.loading = false;
      this.clearRows();
      return;
    }
    this.loading = true;
    this.errorMessage = '';

    this.requestSub = this.pauseEventsService
      .listEvents(companyId, {
        page: this.currentPage,
        limit: this.itemsPerPage,
        ...toPauseEventQuery(this.appliedFilters, this.searchTerm),
      })
      .subscribe({
        next: res => {
          this.loading = false;
          if (res.status && res.data) {
            this.rows = res.data.events.map(toPauseEventRow);
            this.totalItems = res.data.totalItems || 0;
            this.currentPage = res.data.currentPage || this.currentPage;
            this.totalPages = res.data.totalPages || 0;
            this.itemsPerPage = res.data.itemsPerPage || this.itemsPerPage;
            return;
          }
          this.clearRows();
          this.errorMessage = res.message || 'Unable to load pause events.';
        },
        error: err => {
          this.loading = false;
          this.clearRows();
          this.errorMessage = err?.error?.message || err?.message || 'Failed to load pause events.';
        },
      });
  }

  private clearRows(): void {
    this.rows = [];
    this.totalItems = 0;
    this.totalPages = 0;
  }
}
